import { store } from '../store';

// Import des types depuis le backend
export enum RuleType {
  // Règles basées sur les quantités
  MinQuantityPerSupplier = "min_quantity_supplier", // Quantité minimale pour un fournisseur spécifique
  MinQuantityPerProduct = "min_quantity_product", // Quantité minimale pour un produit spécifique
  MaxQuantityPerProduct = "max_quantity_product", // Quantité maximale pour un produit spécifique
  QuantityRange = "quantity_range", // Plage de quantité acceptable (min-max) pour un produit
  
  // Règles basées sur les valeurs monétaires
  MinOrderValue = "min_order_value", // Valeur minimale de commande pour un fournisseur
  MaxOrderValue = "max_order_value", // Valeur maximale de commande pour un fournisseur
  OrderValueRange = "order_value_range", // Plage de valeur acceptable pour une commande
  
  // Règles basées sur les stocks et approvisionnements
  RunwayOverride = "runway_override", // Override du runway pour un produit ou fournisseur spécifique
  AutoOrder = "auto_order", // Commander automatiquement certains produits quand le stock est bas
  
  // Règles liées aux catégories et groupes
  CategoryMinQuantity = "category_min_quantity", // Quantité minimale pour une catégorie de produits
  CategoryMaxQuantity = "category_max_quantity", // Quantité maximale pour une catégorie de produits
  
  // Règles temporelles
  SeasonalAdjustment = "seasonal_adjustment", // Ajustement saisonnier pour certains produits
  DayOfWeekOrder = "day_of_week_order", // Commander uniquement certains jours de la semaine
  
  // Règles de priorité
  SupplierPriority = "supplier_priority", // Priorité de fournisseur pour certains produits
  EmergencyRestock = "emergency_restock", // Règle de réapprovisionnement d'urgence
  
  // Règles spéciales
  CustomFormula = "custom_formula", // Formule personnalisée (règle avancée)
}

export interface SupplyRule {
  id?: string;
  _id?: string;
  name: string;                   // Nom de la règle
  type: RuleType;                 // Type de règle
  active: boolean;                // Si la règle est active ou non
  
  // Conditions (optionnelles)
  supplierId?: string;            // Référence au fournisseur
  supplierName?: string;          // Nom du fournisseur (pour l'affichage)
  
  productId?: string;             // ID du produit
  productName?: string;           // Nom du produit (pour l'affichage)
  
  productCategoryId?: string;     // ID de la catégorie de produit
  productCategoryName?: string;   // Nom de la catégorie (pour l'affichage)
  
  locationId?: string;            // ID de l'emplacement
  locationName?: string;          // Nom de l'emplacement (pour l'affichage)
  
  // Valeurs de règle
  value: number;                  // Valeur principale de la règle
  secondaryValue?: number;        // Valeur secondaire (ex: pour plages)
  unit?: string;                  // Unité de la valeur (ex: "unités", "€")
  priority?: number;              // Priorité de la règle (plus élevé = plus prioritaire)
  
  // Configuration avancée
  daysOfWeek?: number[];          // Jours où la règle s'applique (0-6, 0=dimanche)
  startDate?: Date | string;      // Date de début de validité
  endDate?: Date | string;        // Date de fin de validité
  
  // Autres informations
  notes?: string;                 // Notes ou commentaires sur la règle
  createdBy?: string;             // Utilisateur ayant créé la règle
  
  // Dates de création/modification
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

// Fonction utilitaire pour récupérer le token d'authentification
const getAuthHeader = (): Record<string, string> => {
  const token = store.getState().user?.currentUser?.token;
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// URL de base pour les API des règles d'approvisionnement
const API_URL = `${
  import.meta.env.VITE_API_URL || "http://localhost:5000/api"
}/supply-rules`;

// Normaliser les données reçues pour unifier id et _id
const normalizeRule = (rule: any): SupplyRule => ({
  ...rule,
  id: rule._id || rule.id,
});

/**
 * Récupère toutes les règles d'approvisionnement
 */
export const getAllSupplyRules = async (): Promise<SupplyRule[]> => {
  try {
    const response = await fetch(API_URL, {
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return data.map(normalizeRule);
  } catch (error) {
    console.error("Erreur lors de la récupération des règles d'approvisionnement:", error);
    throw error;
  }
};

/**
 * Récupère toutes les règles d'approvisionnement actives
 */
export const getActiveSupplyRules = async (): Promise<SupplyRule[]> => {
  try {
    const response = await fetch(`${API_URL}/active`, {
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return data.map(normalizeRule);
  } catch (error) {
    console.error("Erreur lors de la récupération des règles actives:", error);
    throw error;
  }
};

/**
 * Récupère une règle par son ID
 * @param id - ID de la règle
 */
export const getSupplyRuleById = async (id: string): Promise<SupplyRule> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return normalizeRule(data);
  } catch (error) {
    console.error(`Erreur lors de la récupération de la règle ${id}:`, error);
    throw error;
  }
};

/**
 * Crée une nouvelle règle d'approvisionnement
 * @param rule - Données de la règle à créer
 */
export const createSupplyRule = async (rule: SupplyRule): Promise<SupplyRule> => {
  try {
    // Créer une copie sans id/_id pour éviter l'erreur "Cast to ObjectId failed"
    const { id, _id, ...ruleData } = rule;
    
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader()
      },
      body: JSON.stringify(ruleData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Erreur lors de la création de la règle:", errorText);
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }
    const data = await response.json();
    return normalizeRule(data);
  } catch (error) {
    console.error("Erreur lors de la création de la règle:", error);
    throw error;
  }
};

/**
 * Met à jour une règle existante
 * @param id - ID de la règle
 * @param rule - Données mises à jour de la règle
 */
export const updateSupplyRule = async (
  id: string,
  rule: Partial<SupplyRule>
): Promise<SupplyRule> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader()
      },
      body: JSON.stringify(rule),
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return normalizeRule(data);
  } catch (error) {
    console.error(`Erreur lors de la mise à jour de la règle ${id}:`, error);
    throw error;
  }
};

/**
 * Active ou désactive une règle
 * @param id - ID de la règle
 */
export const toggleSupplyRule = async (id: string): Promise<{id: string; active: boolean}> => {
  try {
    const response = await fetch(`${API_URL}/${id}/toggle`, {
      method: "PATCH",
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Erreur lors du basculement de la règle ${id}:`, error);
    throw error;
  }
};

/**
 * Supprime une règle
 * @param id - ID de la règle à supprimer
 */
export const deleteSupplyRule = async (id: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      method: "DELETE",
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
  } catch (error) {
    console.error(`Erreur lors de la suppression de la règle ${id}:`, error);
    throw error;
  }
};

/**
 * Récupère les règles filtrées par type
 * @param type - Type de règle à filtrer
 */
export const getSupplyRulesByType = async (type: RuleType): Promise<SupplyRule[]> => {
  try {
    const response = await fetch(`${API_URL}/filter/type/${type}`, {
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return data.map(normalizeRule);
  } catch (error) {
    console.error(`Erreur lors de la récupération des règles de type ${type}:`, error);
    throw error;
  }
};

/**
 * Récupère les règles filtrées par fournisseur
 * @param supplierId - ID du fournisseur
 */
export const getSupplyRulesBySupplier = async (supplierId: string): Promise<SupplyRule[]> => {
  try {
    const response = await fetch(`${API_URL}/filter/supplier/${supplierId}`, {
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return data.map(normalizeRule);
  } catch (error) {
    console.error(`Erreur lors de la récupération des règles pour le fournisseur ${supplierId}:`, error);
    throw error;
  }
};

/**
 * Récupère les règles filtrées par produit
 * @param productId - ID du produit
 */
export const getSupplyRulesByProduct = async (productId: string): Promise<SupplyRule[]> => {
  try {
    const response = await fetch(`${API_URL}/filter/product/${productId}`, {
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return data.map(normalizeRule);
  } catch (error) {
    console.error(`Erreur lors de la récupération des règles pour le produit ${productId}:`, error);
    throw error;
  }
};
